/**
 * Wraps raw little-endian PCM16 audio in a 44-byte RIFF/WAVE header so
 * the result is a Blob that `<audio>` and `Web Audio API` can decode
 * directly. VoxCPM2 streams headerless PCM (sampleRate=48000, mono,
 * 16-bit), and HTMLAudioElement won't play raw PCM — this is the
 * smallest amount of glue to bridge the two without pulling in a
 * decoder.
 *
 * Reference: http://soundfile.sapp.org/doc/WaveFormat/
 */
/**
 * Pure builder — returns the 44-byte WAV header bytes for the supplied
 * PCM length / sample rate / channel count. Exported separately so
 * tests can inspect the byte layout without needing Blob.arrayBuffer
 * (jsdom lacks it).
 */
export function buildWavHeader(
  dataSize: number,
  sampleRate = 48000,
  channels = 1,
): Uint8Array {
  const bytesPerSample = 2;
  const byteRate = sampleRate * channels * bytesPerSample;
  const blockAlign = channels * bytesPerSample;
  const headerSize = 44;
  const buffer = new ArrayBuffer(headerSize);
  const view = new DataView(buffer);

  // RIFF chunk descriptor
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, 'WAVE');

  // fmt sub-chunk
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);          // fmt chunk size (PCM = 16)
  view.setUint16(20, 1, true);           // audio format (1 = PCM)
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);          // bits per sample

  // data sub-chunk
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  return new Uint8Array(buffer);
}

export function pcm16ToWavBlob(
  pcm: Uint8Array,
  sampleRate = 48000,
  channels = 1,
): Blob {
  const header = buildWavHeader(pcm.byteLength, sampleRate, channels);
  // BlobPart in modern lib.dom.d.ts narrows ArrayBufferView's buffer to
  // ArrayBuffer (not ArrayBufferLike), so a vanilla Uint8Array is
  // structurally rejected. Cast to BlobPart since both views are byte-
  // compatible at runtime.
  return new Blob([header as BlobPart, pcm as BlobPart], { type: 'audio/wav' });
}

function writeAscii(view: DataView, offset: number, s: string): void {
  for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
}
